<template>
    <div class="cs-chart-preview" ref="container">
        <div v-if="!result" class="cs-preview-empty">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" opacity="0.3">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
            </svg>
            <p>Run your indicator to see the preview</p>
        </div>
        <canvas v-show="result" ref="canvas" class="cs-canvas"></canvas>
        <div v-if="result" class="cs-preview-info">
            <span v-for="output in outputInfo" :key="output.key" class="cs-output-badge" :style="{ borderColor: output.color }">
                <span class="cs-output-dot" :style="{ background: output.color }"></span>
                {{ output.label }}: {{ output.lastValue }}
            </span>
        </div>
    </div>
</template>

<script>
export default {
    name: 'ChartPreview',
    props: {
        result: { default: null },
        config: { type: Object, default: () => ({}) },
        side: { type: String, default: 'offchart' },
        dark: { type: Boolean, default: false },
    },
    data() {
        return {
            outputInfo: [],
            resizeObserver: null,
        };
    },
    watch: {
        result: {
            handler() {
                this.$nextTick(() => this.render());
            },
            deep: true,
        },
        dark() {
            this.$nextTick(() => this.render());
        },
    },
    mounted() {
        this.resizeObserver = new ResizeObserver(() => {
            this.$nextTick(() => this.render());
        });
        if (this.$refs.container) {
            this.resizeObserver.observe(this.$refs.container);
        }
    },
    beforeDestroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    },
    methods: {
        render() {
            if (!this.result || !this.$refs.canvas) return;

            const canvas = this.$refs.canvas;
            const container = this.$refs.container;
            const dpr = window.devicePixelRatio || 1;

            canvas.width = container.offsetWidth * dpr;
            canvas.height = (container.offsetHeight - 32) * dpr;
            canvas.style.width = container.offsetWidth + 'px';
            canvas.style.height = (container.offsetHeight - 32) + 'px';

            const ctx = canvas.getContext('2d');
            ctx.scale(dpr, dpr);

            const w = container.offsetWidth;
            const h = container.offsetHeight - 32;

            // Clear
            ctx.fillStyle = this.dark ? '#121826' : '#ffffff';
            ctx.fillRect(0, 0, w, h);

            // Parse result into drawable series
            const series = this.parseResult(this.result);
            if (!series.length) return;

            // Draw grid
            this.drawGrid(ctx, w, h);

            // Calculate Y range
            let yMin = Infinity, yMax = -Infinity;
            for (const s of series) {
                for (const v of s.data) {
                    if (!isNaN(v) && isFinite(v)) {
                        yMin = Math.min(yMin, v);
                        yMax = Math.max(yMax, v);
                    }
                }
            }

            if (yMin === Infinity) return;

            const padding = (yMax - yMin) * 0.1 || 1;
            yMin -= padding;
            yMax += padding;

            // Draw bands if configured
            if (this.config.bands) {
                for (const band of this.config.bands) {
                    this.drawBand(ctx, w, h, band, yMin, yMax);
                }
            }

            // Draw fill if configured
            if (this.config.fill && series.length >= 2) {
                this.drawFill(ctx, w, h, series, yMin, yMax);
            }

            // Draw each series
            const outputs = (this.config.outputs || []);
            this.outputInfo = [];

            for (let si = 0; si < series.length; si++) {
                const s = series[si];
                const outputConf = outputs[si] || {};
                const color = outputConf.color || this.getDefaultColor(si);
                const style = outputConf.style || 'line';
                const lineWidth = outputConf.width || 1.5;

                if (style === 'histogram') {
                    this.drawHistogram(ctx, w, h, s.data, color, yMin, yMax);
                } else {
                    this.drawLine(ctx, w, h, s.data, color, lineWidth, style === 'dashed', yMin, yMax);
                }

                // Output info
                const lastVal = this.getLastValue(s.data);
                this.outputInfo.push({
                    key: s.key || 'output' + si,
                    label: outputConf.label || s.key || 'Output ' + (si + 1),
                    color,
                    lastValue: lastVal !== null ? lastVal.toFixed(2) : 'N/A',
                });
            }

            // Draw Y axis labels
            this.drawYAxis(ctx, w, h, yMin, yMax);
        },

        parseResult(result) {
            if (!result) return [];

            // Array of objects with 'value' key
            if (Array.isArray(result)) {
                if (result.length === 0) return [];
                if (typeof result[0] === 'object' && result[0] !== null) {
                    // Extract 'value' key
                    const data = result.map(r => r.value !== undefined ? r.value : NaN);
                    return [{ key: 'value', data }];
                }
                // Plain number array
                if (typeof result[0] === 'number') {
                    return [{ key: 'value', data: result }];
                }
                return [];
            }

            // Object with multiple outputs
            if (typeof result === 'object') {
                const series = [];
                const outputs = (this.config.outputs || []).map(o => o.key);

                // Use output order from config if available
                const keys = outputs.length > 0
                    ? outputs.filter(k => result[k] !== undefined)
                    : Object.keys(result).filter(k => Array.isArray(result[k]));

                for (const key of keys) {
                    const data = result[key];
                    if (Array.isArray(data)) {
                        series.push({ key, data: data.map(v => typeof v === 'number' ? v : NaN) });
                    }
                }
                return series;
            }

            return [];
        },

        drawGrid(ctx, w, h) {
            ctx.strokeStyle = this.dark ? 'rgba(55,59,75,0.4)' : 'rgba(224,227,235,0.6)';
            ctx.lineWidth = 0.5;

            // Horizontal lines
            for (let i = 1; i < 5; i++) {
                const y = (h / 5) * i;
                ctx.beginPath();
                ctx.moveTo(0, y);
                ctx.lineTo(w - 50, y);
                ctx.stroke();
            }
        },

        drawLine(ctx, w, h, data, color, lineWidth, dashed, yMin, yMax) {
            const len = data.length;
            if (len === 0) return;

            const xStep = (w - 60) / len;
            const yRange = yMax - yMin;

            ctx.strokeStyle = color;
            ctx.lineWidth = lineWidth;
            ctx.lineJoin = 'round';
            ctx.lineCap = 'round';

            if (dashed) {
                ctx.setLineDash([6, 4]);
            } else {
                ctx.setLineDash([]);
            }

            ctx.beginPath();
            let started = false;

            for (let i = 0; i < len; i++) {
                const v = data[i];
                if (isNaN(v) || !isFinite(v)) {
                    started = false;
                    continue;
                }

                const x = 10 + i * xStep;
                const y = h - 20 - ((v - yMin) / yRange) * (h - 40);

                if (!started) {
                    ctx.moveTo(x, y);
                    started = true;
                } else {
                    ctx.lineTo(x, y);
                }
            }
            ctx.stroke();
            ctx.setLineDash([]);
        },

        drawHistogram(ctx, w, h, data, color, yMin, yMax) {
            const len = data.length;
            if (len === 0) return;

            const xStep = (w - 60) / len;
            const yRange = yMax - yMin;
            const zeroY = h - 20 - ((0 - yMin) / yRange) * (h - 40);
            const barWidth = Math.max(1, xStep * 0.6);

            for (let i = 0; i < len; i++) {
                const v = data[i];
                if (isNaN(v) || !isFinite(v)) continue;

                const x = 10 + i * xStep;
                const y = h - 20 - ((v - yMin) / yRange) * (h - 40);

                ctx.fillStyle = v >= 0
                    ? (this.dark ? 'rgba(34,197,94,0.6)' : 'rgba(34,197,94,0.5)')
                    : (this.dark ? 'rgba(239,68,68,0.6)' : 'rgba(239,68,68,0.5)');

                const barH = zeroY - y;
                ctx.fillRect(x - barWidth / 2, Math.min(y, zeroY), barWidth, Math.abs(barH));
            }
        },

        drawBand(ctx, w, h, band, yMin, yMax) {
            const yRange = yMax - yMin;
            const y = h - 20 - ((band.value - yMin) / yRange) * (h - 40);

            ctx.strokeStyle = band.color || '#aaa';
            ctx.lineWidth = 1;
            ctx.setLineDash([4, 4]);
            ctx.beginPath();
            ctx.moveTo(10, y);
            ctx.lineTo(w - 50, y);
            ctx.stroke();
            ctx.setLineDash([]);

            // Label
            ctx.fillStyle = band.color || '#aaa';
            ctx.font = '10px Inter, sans-serif';
            ctx.fillText(band.label || band.value, w - 48, y + 3);
        },

        drawFill(ctx, w, h, series, yMin, yMax) {
            if (series.length < 2) return;
            const fill = this.config.fill;
            if (!fill) return;

            const fromIdx = series.findIndex(s => s.key === fill.from);
            const toIdx = series.findIndex(s => s.key === fill.to);
            if (fromIdx < 0 || toIdx < 0) return;

            const fromData = series[fromIdx].data;
            const toData = series[toIdx].data;
            const len = Math.min(fromData.length, toData.length);
            const xStep = (w - 60) / len;
            const yRange = yMax - yMin;

            ctx.fillStyle = fill.color || 'rgba(41,98,255,0.06)';
            ctx.beginPath();

            // Forward path (from series)
            let started = false;
            for (let i = 0; i < len; i++) {
                if (isNaN(fromData[i])) continue;
                const x = 10 + i * xStep;
                const y = h - 20 - ((fromData[i] - yMin) / yRange) * (h - 40);
                if (!started) { ctx.moveTo(x, y); started = true; }
                else ctx.lineTo(x, y);
            }

            // Reverse path (to series)
            for (let i = len - 1; i >= 0; i--) {
                if (isNaN(toData[i])) continue;
                const x = 10 + i * xStep;
                const y = h - 20 - ((toData[i] - yMin) / yRange) * (h - 40);
                ctx.lineTo(x, y);
            }

            ctx.closePath();
            ctx.fill();
        },

        drawYAxis(ctx, w, h, yMin, yMax) {
            ctx.fillStyle = this.dark ? '#787b86' : '#9ca3af';
            ctx.font = '10px Inter, sans-serif';
            ctx.textAlign = 'right';

            const steps = 5;
            for (let i = 0; i <= steps; i++) {
                const val = yMin + (yMax - yMin) * (i / steps);
                const y = h - 20 - (i / steps) * (h - 40);
                ctx.fillText(val.toFixed(2), w - 4, y + 3);
            }
        },

        getLastValue(data) {
            for (let i = data.length - 1; i >= 0; i--) {
                if (!isNaN(data[i]) && isFinite(data[i])) return data[i];
            }
            return null;
        },

        getDefaultColor(index) {
            const colors = ['#2962ff', '#ff6d00', '#7e57c2', '#26a69a', '#ef5350', '#ab47bc'];
            return colors[index % colors.length];
        },
    },
};
</script>

<style scoped>
.cs-chart-preview {
    height: 100%;
    display: flex;
    flex-direction: column;
    position: relative;
}

.cs-preview-empty {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: var(--tc-text-tertiary, #9ca3af);
    font-size: 13px;
    gap: 12px;
}

.cs-canvas {
    flex: 1;
    display: block;
}

.cs-preview-info {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 6px 12px;
    border-top: 1px solid var(--tc-border-secondary, #f0f3fa);
    flex-shrink: 0;
    flex-wrap: wrap;
    min-height: 32px;
}

.cs-output-badge {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    font-weight: 500;
    color: var(--tc-text-secondary, #787b86);
    padding: 2px 8px;
    border: 1px solid;
    border-radius: 4px;
    font-family: 'SF Mono', 'Fira Code', monospace;
}

.cs-output-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
}
</style>
